/**
 * Template Validation Schemas
 *
 * Zod schemas for validating plan and workout template creation/editing.
 * These schemas match the Laravel API field names (snake_case).
 *
 * Separate from workout.schemas.ts which handles workout session logging.
 */

import {
  templateNameSchema as nameSchema,
  templateDescriptionSchema as descriptionSchema,
  difficultyLevelSchema,
  trackingTypeSchema,
  groupTypeSchema,
  setTypeSchema,
} from './common.schemas';
import { z } from './zod';

// =============================================================================
// PLAN TEMPLATE SCHEMAS
// =============================================================================

/** How many decimal places a set's weight, duration and distance may have. */
export const SET_VALUE_DECIMAL_PLACES = 3;

/** Whether a number has no more than `places` decimal places. */
export function hasAtMostDecimalPlaces(value: number, places: number): boolean {
  const scaled = value * 10 ** places;
  // A tolerance, because 0.1 + 0.2 is not 0.3: 1.005 * 1000 is 1004.9999….
  return Math.abs(scaled - Math.round(scaled)) < 1e-6;
}

/**
 * A measured set value — weight, duration or distance: above zero, with up to
 * three decimal places, as the API stores them (62.5 kg, 0.25 mi).
 */
const measuredValueSchema = z
  .number()
  .positive()
  .refine(
    (value) => hasAtMostDecimalPlaces(value, SET_VALUE_DECIMAL_PLACES),
    `Use at most ${SET_VALUE_DECIMAL_PLACES} decimal places`
  );

/**
 * Exercise set schema (target sets for templates). Reps are counted, so whole;
 * the rest are measured.
 */
export const templateSetSchema = z.object({
  uuid: z.string().min(1),
  type: setTypeSchema,
  reps: z.number().int().min(1).nullable().optional(),
  weight: measuredValueSchema.nullable().optional(),
  duration: measuredValueSchema.nullable().optional(),
  distance: measuredValueSchema.nullable().optional(),
});

/**
 * Exercise schema within a workout template
 */
export const templateExerciseSchema = z.object({
  uuid: z.string().min(1),
  id: z.string().nullable().optional(),
  name: nameSchema,
  guid: z.string().min(1, 'Exercise GUID is required'),
  tracking_type: trackingTypeSchema,
  gif_url: z.string().nullable().optional(),
  group_leader: z.string().nullable().optional(),
  group_type: groupTypeSchema.optional(),
  sets: z.array(templateSetSchema).nullable().optional(),
});

/**
 * Workout schema within a plan template
 */
export const templateWorkoutSchema = z.object({
  uuid: z.string().min(1),
  id: z.string().nullable().optional(),
  name: nameSchema,
  description: descriptionSchema,
  difficulty_level: difficultyLevelSchema.optional(),
  rest_duration: z
    .number()
    .int()
    .min(10, 'Rest duration must be at least 10 seconds')
    .multipleOf(10, 'Rest duration must be a multiple of 10'),
  exercises: z.array(templateExerciseSchema).nullable().optional(),
});

/** Shortest deload cadence the API accepts — `analytics.periodization.deload.schedule_min_weeks`. */
export const DELOAD_EVERY_WEEKS_MIN = 2;
/** Longest deload cadence the API accepts — `analytics.periodization.deload.schedule_max_weeks`. */
export const DELOAD_EVERY_WEEKS_MAX = 12;

const deloadRangeMessage = `A deload cadence must be between ${DELOAD_EVERY_WEEKS_MIN} and ${DELOAD_EVERY_WEEKS_MAX} weeks`;

/**
 * A plan's opt-in deload cadence: every Nth week of the plan is an easier one.
 * `null` means no scheduled deload.
 *
 * Optional as well as nullable, and the two differ on the wire: `planService`
 * sends the parsed output, so a field this schema does not list is stripped
 * before the request, and on a plan proposal an omitted key leaves the client's
 * cadence alone while `null` clears it.
 */
export const deloadEveryWeeksSchema = z
  .number()
  .int(deloadRangeMessage)
  .min(DELOAD_EVERY_WEEKS_MIN, deloadRangeMessage)
  .max(DELOAD_EVERY_WEEKS_MAX, deloadRangeMessage)
  .nullable()
  .optional();

/**
 * Plan template schema - full nested structure
 */
export const planTemplateSchema = z.object({
  uuid: z.string().min(1),
  id: z.string().nullable().optional(),
  name: nameSchema,
  description: descriptionSchema,
  difficulty_level: difficultyLevelSchema,
  active: z.boolean(),
  deload_every_weeks: deloadEveryWeeksSchema,
  workouts: z.array(templateWorkoutSchema).nullable().optional(),
});

/**
 * Standalone workout template schema (for creating workouts outside a plan)
 */
export const workoutTemplateSchema = z.object({
  name: nameSchema,
  description: descriptionSchema,
  difficulty_level: difficultyLevelSchema,
  rest_duration: z
    .number()
    .int()
    .min(10, 'Rest duration must be at least 10 seconds')
    .multipleOf(10, 'Rest duration must be a multiple of 10'),
  plan: z.string().nullable().optional(),
  exercises: z.array(templateExerciseSchema).nullable().optional(),
});

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type TemplateSetInput = z.input<typeof templateSetSchema>;
export type TemplateSetOutput = z.output<typeof templateSetSchema>;

export type TemplateExerciseInput = z.input<typeof templateExerciseSchema>;
export type TemplateExerciseOutput = z.output<typeof templateExerciseSchema>;

export type TemplateWorkoutInput = z.input<typeof templateWorkoutSchema>;
export type TemplateWorkoutOutput = z.output<typeof templateWorkoutSchema>;

export type PlanTemplateInput = z.input<typeof planTemplateSchema>;
export type PlanTemplateOutput = z.output<typeof planTemplateSchema>;

export type WorkoutTemplateInput = z.input<typeof workoutTemplateSchema>;
export type WorkoutTemplateOutput = z.output<typeof workoutTemplateSchema>;

// =============================================================================
// VALIDATION HELPERS - Plan Template
// =============================================================================

export function validatePlanTemplate(data: unknown): PlanTemplateOutput {
  return planTemplateSchema.parse(data);
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidatePlanTemplate(data: unknown) {
  return planTemplateSchema.safeParse(data);
}

export function isValidPlanTemplate(data: unknown): boolean {
  return planTemplateSchema.safeParse(data).success;
}

// =============================================================================
// VALIDATION HELPERS - Workout Template
// =============================================================================

export function validateWorkoutTemplate(data: unknown): WorkoutTemplateOutput {
  return workoutTemplateSchema.parse(data);
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateWorkoutTemplate(data: unknown) {
  return workoutTemplateSchema.safeParse(data);
}

export function isValidWorkoutTemplate(data: unknown): boolean {
  return workoutTemplateSchema.safeParse(data).success;
}
